import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle2, AlertTriangle, Info, TrendingUp, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Audit = () => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const response = await axios.get(`${API}/audits`, {
          withCredentials: true
        });
        setAuditData(response.data);
      } catch (error) {
        console.error('Error fetching audit:', error);
        toast.error('Failed to load audit data');
      } finally {
        setLoading(false);
      }
    };

    fetchAudit();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Running audit...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isFree = auditData?.tier === 'free';
  const audit = isFree ? auditData?.basic_audit : auditData?.comprehensive_audit;

  const getIcon = (type) => {
    switch (type) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-red-500 bg-red-500/10';
      case 'high':
        return 'text-orange-500 bg-orange-500/10';
      case 'medium':
        return 'text-yellow-500 bg-yellow-500/10';
      default:
        return 'text-blue-500 bg-blue-500/10';
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="audit-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Campaign Audit</h1>
          <p className="text-muted-foreground">AI-powered insights and recommendations for optimization</p>
        </div>

        {/* Overall Score */}
        <Card className="p-8 mb-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-primary/10 mb-4">
              <span className="text-5xl font-bold text-primary">{audit?.overall_score || 0}</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Overall Performance Score</h2>
            <p className="text-muted-foreground">Your campaigns are performing reasonably well with room for improvement</p>
          </div>
          <Progress value={audit?.overall_score || 0} className="h-2" />
        </Card>

        {/* Category Scores (Premium Only) */}
        {!isFree && audit?.category_scores && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {Object.entries(audit.category_scores).map(([key, value]) => (
              <Card key={key} className="p-4">
                <div className="text-sm text-muted-foreground mb-2 capitalize">
                  {key.replace('_', ' ')}
                </div>
                <div className="text-2xl font-bold mb-2">{value}</div>
                <Progress value={value} className="h-1" />
              </Card>
            ))}
          </div>
        )}

        {/* Findings */}
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-primary" />
            Key Findings
          </h3>
          <div className="space-y-3">
            {audit?.findings?.map((finding, idx) => (
              <div key={idx} className="flex items-start space-x-3 p-4 rounded-lg bg-card border border-border">
                {getIcon(finding.type)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{finding.category}</span>
                    {finding.impact && (
                      <span className="text-xs text-muted-foreground">{finding.impact}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{finding.message}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recommendations */}
        <Card className="p-6 mb-8">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-primary" />
            Recommendations
          </h3>
          <div className="space-y-4">
            {audit?.recommendations?.map((rec, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-card border border-border">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-bold">{rec.title}</h4>
                  <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(rec.priority)}`}>
                    {rec.priority}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
                {rec.expected_impact && (
                  <div className="flex items-center space-x-2 text-sm text-green-500">
                    <TrendingUp className="w-4 h-4" />
                    <span>Expected Impact: {rec.expected_impact}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Gap Analysis (Premium Only) */}
        {!isFree && audit?.gap_analysis && (
          <Card className="p-6 mb-8">
            <h3 className="text-xl font-bold mb-4">Gap Analysis</h3>
            <div className="space-y-4">
              {audit.gap_analysis.map((gap, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-card border border-border">
                  <h4 className="font-bold mb-2">{gap.area}</h4>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Current State</span>
                      <p className="font-medium">{gap.current_state}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Best Practice</span>
                      <p className="font-medium">{gap.best_practice}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Gap</span>
                      <p className="font-medium text-orange-500">{gap.gap}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Upgrade CTA for Free Users */}
        {isFree && (
          <Card className="p-8 bg-primary text-primary-foreground text-center">
            <h3 className="text-2xl font-bold mb-2">Unlock Comprehensive Insights</h3>
            <p className="mb-6 opacity-90">
              Upgrade to Pro for detailed category scores, gap analysis, and AI-powered recommendations
            </p>
            <Button data-testid="upgrade-button" className="bg-primary-foreground text-primary hover:opacity-90">
              Upgrade to Pro
            </Button>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Audit;
